import {Component, EventEmitter, HostListener, Input, OnInit, Output, ViewChild} from '@angular/core';
import {ActivatedRoute, Router, UrlSegment} from '@angular/router';
import {MessageService} from 'primeng/api';
import {translate, TranslocoService} from '@ngneat/transloco';
import {User, UserApplications, UserCredentials} from '../../../model/user';
import {UserPersistRequest} from '../../../model/requests/user-persist-request';
import {UsersService} from '../../../services/users.service';
import {ComponentCanDeactivate} from '../../../common/can-deactivate/component-can-deactivate';
import {UsersFormInfoComponent} from './users-form-info/users-form-info.component';
import {LoggedUser} from '../../../common/logged-user.service';
import {AuthGuard} from '../../../common/auth-guard.service';
import {HttpErrorResponse} from '@angular/common/http';
import {AppHttpInterceptor} from '../../../common/http-interceptor/filtered-http';
import {CommonMessagesService} from '../../../services/common-messages.service';

@Component({
  selector: 'app-users-form',
  templateUrl: './users-form.component.html',
  styleUrls: ['./users-form.component.scss']
})
export class UsersFormComponent implements OnInit, ComponentCanDeactivate {

  private static readonly DEFAULT_TIME_ZONE = 'Europe/Madrid';
  private static readonly DEFAULT_LANGUAGE = 'es';

  @Input() active: boolean;
  @Output() activeChange = new EventEmitter();

  items: any[];
  currentStep: number;

  user: User;
  userCredentials: UserCredentials;
  userApplications: UserApplications;
  persistingChanges: boolean;
  formLoaded: boolean;
  formSuccess: boolean;

  mode: 'create' | 'edit';

  public requiredUserActions;

  @ViewChild(UsersFormInfoComponent)
  usersFormInfo: UsersFormInfoComponent;

  constructor(private usersService: UsersService,
              private route: ActivatedRoute,
              private router: Router,
              private i18n: TranslocoService,
              private loggedUser: LoggedUser,
              private authGuard: AuthGuard,
              private messageService: MessageService,
              private commonMessagesService: CommonMessagesService,
              private appHttpInterceptor: AppHttpInterceptor) {
  }

  ngOnInit(): void {
    this.currentStep = 0;

    this.i18n.selectTranslate('model.device').subscribe(() => {
      this.items = [
        {
          index: 0,
          icon: 'pi pi-user',
          label: this.i18n.translate('model.userinfo'),
          description: this.i18n.translate('forms.descriptions.user')
        },
        {
          index: 1,
          icon: 'pi pi-key',
          label: this.i18n.translate('model.credentials'),
          description: this.i18n.translate('forms.descriptions.credentials')
        },
        {
          index: 2,
          icon: 'pi pi-th-large',
          label: this.i18n.translate('model.applications'),
          description: this.i18n.translate('forms.descriptions.userapplications')
        },
        {index: 3, icon: 'pi pi-check', label: this.i18n.translate('forms.confirmation')}
      ];
    });

    this.i18n.selectTranslate('common.new').subscribe(() => {
      this.requiredUserActions = [
        {name: translate('misc.userActions.updatePassword'), code: 'UPDATE_PASSWORD', icon: 'pi pi-lock'},
        {name: translate('misc.userActions.updateProfile'), code: 'UPDATE_PROFILE', icon: 'pi pi-user'},
        {name: translate('misc.userActions.configureOtp'), code: 'CONFIGURE_TOTP', icon: 'pi pi-cog'},
        {name: translate('misc.userActions.verifyEmail'), code: 'VERIFY_EMAIL', icon: 'pi pi-envelope'}
      ];
    });

    this.route.url.subscribe(urlSegment => {
      const firstPath = urlSegment[0].path;
      if (firstPath === 'new') {
        this.mode = 'create';
        this.loadCreationForm();
      } else {
        this.mode = 'edit';
        this.loadEditionForm(urlSegment);
      }
    });
  }

  loadCreationForm(): void {
    this.usersService.getApplicationsName().toPromise().then(applications => {
      this.loggedUser.userObservable.subscribe(user => {
        this.user = new User();
        this.user.enabled = true;
        this.user.emailVerified = false;
        this.user.requieredActions = [];
        this.user.language = UsersFormComponent.DEFAULT_LANGUAGE;
        this.user.timeZone = UsersFormComponent.DEFAULT_TIME_ZONE;
        this.userCredentials = {temporal: true, confirmValue: '', value: ''};
        this.userApplications = {
          available: applications.availableApplications,
          superadmin: [],
          admin: [],
          viewer: [],
          operator: []
        };

        if (!this.authGuard.superAdminRequired()) {
          this.user.tenantUUID = user.tenant;
          if (user.organization) {
            this.user.organizationUUID = user.organization;
          }
        }
        this.formLoaded = true;
      });
    });
  }

  loadEditionForm(urlSegment: UrlSegment[]): void {
    this.userApplications = {available: [], superadmin: [], admin: [], viewer: [], operator: []};
    const promises = [
      this.usersService.getUser(urlSegment[0].path, urlSegment[1].path).toPromise()
        .then(response => {
          this.user = response.user;
          this.userCredentials = {temporal: false, confirmValue: '', value: ''};
          this.userApplications.superadmin = response.user.superadminApplications;
          this.userApplications.admin = response.user.adminApplications;
          this.userApplications.viewer = response.user.viewerApplications;
          this.userApplications.operator = response.user.operatorApplications;
        }, error => {
          if (error.status === 404) {
            this.router.navigate(['/not-found']);
          }
        }),
      this.usersService.getApplicationsName().toPromise().then(applications => {
        this.userApplications.available = applications.availableApplications;
      })
    ];

    Promise.all(promises).then(() => {
      const allUserApps = [].concat(this.userApplications.superadmin)
        .concat(this.userApplications.admin)
        .concat(this.userApplications.viewer)
        .concat(this.userApplications.operator);
      this.userApplications.available = this.userApplications.available.filter(value => allUserApps.indexOf(value) === -1);
      this.formLoaded = true;
    });
  }

  persistUserData(): void {
    this.persistingChanges = true;
    const persistData = new UserPersistRequest(this.user, this.userCredentials, this.userApplications);

    let promise;
    if (this.mode === 'create') {
      promise = this.usersService.createUser(persistData).toPromise();
    } else {
      promise = this.usersService.editUser(persistData).toPromise();
    }

    promise.then((): void => {
      if (this.active) {
        this.active = false;
        this.activeChange.emit(this.active);
      }
      this.formSuccess = true;

      const detailMessage: string = 'success.users.' + this.mode;
      const summary = this.i18n.translate('success.generic.changesApplied');
      const detail = this.i18n.translate(detailMessage);
      this.messageService.add({severity: 'success', summary, detail, sticky: false});

      this.router.navigate(([`/management/users`]));
    }).catch((error: HttpErrorResponse): void => {
      if (!this.appHttpInterceptor.isHttpCodeFiltered(error.status)){
        const detailMessage: string = error.status === 409 ? 'error.users.alreadyExist' : 'error.' + this.mode + '.unexpected';
        const summary = this.i18n.translate('error.summary.unexpected');
        const detail = this.i18n.translate(detailMessage);
        this.messageService.add({severity: 'error', summary, detail, sticky: true});
      }

      this.persistingChanges = false;
    });
  }

  nextStep(): void {
    if (this.currentStep < this.items.length - 1) {
      this.currentStep++;
    }
  }

  previousStep(): void {
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  cancelAction(): void {
    this.router.navigate(([`/management/users`]));

  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || (this.currentStep === 0 && this.usersFormInfo.userForm.untouched);
  }

}
